/**
 * Route helper utilities for Pages.
 *
 * Each admin sidebar renders its own list of Pages and needs to resolve three
 * routes per site context: the public configuration link for a given slug, the
 * POST endpoint to create a new page, and the DELETE endpoint to remove one.
 *
 * 'code_medical_center' is a normal site context now: its custom pages live
 * under /admin/code-medical-center just like every clinic. The same string is
 * stored in pages.site_context server-side, so passing it as a JSON key or a
 * prop value works end-to-end with no branching.
 */

export const ROOT_SITE_CONTEXT = 'code_medical_center';

const ROUTE_MAP = {
  [ROOT_SITE_CONTEXT]: {
    configuration: (routes, slug) => routes.admin_code_medical_center_configuration({ page: slug }),
    create: (routes) => routes.admin_code_medical_center_pages(),
    destroy: (routes, id) => routes.admin_code_medical_center_page(id),
    pathPrefix: 'admin/code-medical-center/strony',
  },
  infertility_treatment_clinic: {
    configuration: (routes, slug) =>
      routes.admin_infertility_treatment_clinic_configuration({ page: slug }),
    create: (routes) => routes.admin_infertility_treatment_clinic_pages(),
    destroy: (routes, id) => routes.admin_infertility_treatment_clinic_page(id),
    pathPrefix: 'admin/infertility-treatment-clinic/strony',
  },
  institute: {
    configuration: (routes, slug) => routes.admin_institute_configuration({ page: slug }),
    create: (routes) => routes.admin_institute_pages(),
    destroy: (routes, id) => routes.admin_institute_page(id),
    pathPrefix: 'admin/institute/strony',
  },
  family_clinic: {
    configuration: (routes, slug) => routes.admin_family_clinic_configuration({ page: slug }),
    create: (routes) => routes.admin_family_clinic_pages(),
    destroy: (routes, id) => routes.admin_family_clinic_page(id),
    pathPrefix: 'admin/family-clinic/strony',
  },
};

function configFor(siteContext) {
  const key = siteContext || ROOT_SITE_CONTEXT;
  return ROUTE_MAP[key] || ROUTE_MAP[ROOT_SITE_CONTEXT];
}

/**
 * Create a route helper bound to a site context and the global $routes object.
 * @param {string} siteContext
 * @param {Object} routes - usually `this.$routes`
 * @returns {{ configuration: (slug) => string, create: () => string, destroy: (id) => string, pathPrefix: string }}
 */
export function createPageRouteHelper(siteContext, routes) {
  const cfg = configFor(siteContext);
  return {
    configuration: (slug) => cfg.configuration(routes, slug),
    create: () => cfg.create(routes),
    destroy: (id) => cfg.destroy(routes, id),
    pathPrefix: cfg.pathPrefix,
  };
}

/**
 * Resolve the Inertia shared-prop key used for pages of a given site context.
 * @param {string} siteContext
 * @returns {string}
 */
export function pagesPropKeyFor(siteContext) {
  return siteContext || ROOT_SITE_CONTEXT;
}
